import { Button, Text } from '@mantine/core'
import { observer } from 'mobx-react-lite'

import {
  showAsyncConfirmModal,
  showError,
  showTransferOwnershipModal,
} from '#components/Modals'
import { STRIPE_TIER } from '#components/Settings/Org/plans'
import { useOrgBillingPlan } from '#components/Settings/Org/useBillingPlan'
import { useMst } from '#state'
import { IOrgMember } from '#state/types'

import { useRemoveMember } from './useSeatChanges'

import styles from './MemberAction.module.scss'

export const MemberAction = observer(function MemberAction({
  member,
}: {
  member: IOrgMember
}) {
  const { currentOrg, user: currentUser } = useMst()
  const isOrgPrivateEnabled = currentOrg?.hasPrivateScriptsEnabled
  const plan = useOrgBillingPlan(currentOrg).data
  const keepsSeat = plan?.seatMode === 'static'

  const memberRemoval = useRemoveMember(currentOrg?.id)

  const removeMember = async (userId: string) => {
    try {
      if (userId === currentUser.id) {
        await member.removeSelf()
        // nuclear option to assign another default show or hint to create
        window.location.replace(window.location.origin)
      } else {
        await memberRemoval.mutateAsync(member)
      }
    } catch (e) {
      showError({ message: 'Failed to update user' })
    }
  }

  const memberCount: number = currentOrg?.members.length ?? 0
  // Hidden on Stripe workspaces until downgrades are handled gracefully.
  const isSubscribed = currentOrg?.tier === STRIPE_TIER
  if (
    member.isOwner &&
    currentUser.isCurrentOrgOwner &&
    memberCount > 1 &&
    !isSubscribed
  ) {
    return (
      <Button
        className={styles.memberAction_button}
        variant="subtle"
        onClick={() => {
          if (currentOrg) showTransferOwnershipModal(currentOrg)
        }}
      >
        Transfer Ownership
      </Button>
    )
  } else if (currentUser.id === member.id && !currentUser.isCurrentOrgOwner) {
    return (
      <Button
        className={styles.memberAction_button}
        variant="subtle"
        color="red"
        onClick={() =>
          openRemoveUserModal({
            member,
            isUserSelf: true,
            isOrgPrivateEnabled,
            keepsSeat,
            onConfirm: removeMember,
          })
        }
        loading={member.isRemoving}
      >
        Leave Workspace
      </Button>
    )
  } else if (currentUser.isCurrentOrgAdmin && !member.isOwner) {
    return (
      <Button
        className={styles.memberAction_button}
        variant="subtle"
        color="red"
        onClick={() =>
          openRemoveUserModal({
            member,
            isUserSelf: false,
            isOrgPrivateEnabled,
            keepsSeat,
            onConfirm: removeMember,
          })
        }
        loading={member.isRemoving}
      >
        Remove Member
      </Button>
    )
  }
  return null
})

function openRemoveUserModal({
  member,
  isUserSelf = false,
  isOrgPrivateEnabled = true,
  keepsSeat = false,
  onConfirm,
}: {
  member: IOrgMember
  isUserSelf?: boolean
  isOrgPrivateEnabled?: boolean
  keepsSeat?: boolean
  onConfirm: (id: string) => Promise<void>
}) {
  showAsyncConfirmModal({
    title: 'Remove Member',
    children: (
      <>
        <Text>
          Are you sure you want to remove&thinsp;
          <strong>{isUserSelf ? 'yourself' : member.name}</strong>?
        </Text>
        {isOrgPrivateEnabled ? (
          <Text>
            {isUserSelf ? 'You' : 'They'} will lose access to the workspace and
            any private scripts will be permanently deleted.
          </Text>
        ) : null}
        {keepsSeat ? (
          <Text>
            {isUserSelf ? 'Your' : 'Their'} vacated seat will remain available
            until your annual plan renews.
          </Text>
        ) : null}
      </>
    ),
    confirmLabel: 'Remove',
    onConfirm: () => onConfirm(member.id),
    errorMessage: 'Failed to remove member',
    dangerous: true,
  })
}
